import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { login, setToken } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [exchanging, setExchanging] = useState(false);

    // Handle Emergent Google callback (URL fragment #session_id=...)
    useEffect(() => {
        const hash = window.location.hash || location.hash || "";
        if (!hash.includes("session_id=")) return;
        const params = new URLSearchParams(hash.replace(/^#/, ""));
        const sid = params.get("session_id");
        if (!sid) return;
        setExchanging(true);
        (async () => {
            try {
                const res = await api.post("/auth/google/session", { session_id: sid });
                localStorage.setItem("dw_token", res.data.token);
                if (typeof setToken === "function") setToken(res.data.token);
                // clean URL fragment
                window.history.replaceState({}, "", window.location.pathname);
                toast.success(`Welcome, ${res.data.user.name || "friend"}`);
                window.location.href = res.data.user.role === "admin" ? "/admin" : "/";
            } catch (e) {
                console.error(e);
                toast.error(e?.response?.data?.detail || "Google sign-in failed. Please try again.");
                window.history.replaceState({}, "", window.location.pathname);
            } finally {
                setExchanging(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(`Welcome back, ${u.name}`);
            const params = new URLSearchParams(location.search);
            const redirect = params.get("redirect");
            if (redirect) {
                navigate(decodeURIComponent(redirect));
            } else {
                navigate(u.role === "admin" ? "/admin" : "/");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/login";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    if (exchanging) {
        return (
            <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="login-google-exchanging">
                <div className="rounded-2xl border border-border bg-card p-10">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-[hsl(var(--accent))]" />
                    <p className="font-display text-2xl mt-6">Signing you in…</p>
                    <p className="text-muted-foreground text-sm mt-2">Verifying your Google session.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto px-6 py-2" data-testid="login-page">
            {new URLSearchParams(location.search).get("reason") === "admin" && (
                <div className="mb-4 rounded-2xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 p-4 text-sm" data-testid="login-admin-required-notice">
                    Please sign in with an administrator account to access X-Plosiv Studio.
                </div>
            )}
            <div className="rounded-2xl border border-border bg-card p-8">
                <h1 className="font-display text-4xl">Welcome back.</h1>
                <p className="text-muted-foreground mt-2 text-sm">Sign in to continue.</p>

                <button
                    onClick={googleLogin}
                    className="mt-7 w-full dw-btn-secondary h-12 font-mono-ui flex items-center justify-center gap-3"
                    data-testid="login-google-button"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                        <path fill="#4285f4" d="M22.5 12.27c0-.85-.07-1.65-.2-2.43H12v4.6h5.93c-.26 1.37-1.04 2.53-2.21 3.31v2.74h3.57c2.08-1.92 3.21-4.74 3.21-8.22z" />
                        <path fill="#34a853" d="M12 23c2.97 0 5.46-.99 7.28-2.66l-3.57-2.74c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.93-6.13-4.52H2.2v2.85C4 20.34 7.7 23 12 23z" />
                        <path fill="#fbbc05" d="M5.87 14.14a6.86 6.86 0 010-4.28V7H2.2A11 11 0 001 12c0 1.78.43 3.46 1.2 5l3.67-2.86z" />
                        <path fill="#ea4335" d="M12 5.45c1.61 0 3.06.55 4.2 1.64l3.15-3.15C17.46 2.04 14.97 1 12 1 7.7 1 4 3.66 2.2 7l3.67 2.85C6.73 7.38 9.15 5.45 12 5.45z" />
                    </svg>
                    Continue with Google
                </button>

                <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground font-mono-ui">or sign in with email</span>
                    <div className="flex-1 h-px bg-border" />
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <Label className="font-mono-ui mb-2 block">Email</Label>
                        <Input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="rounded-2xl bg-background/60 border-border h-11"
                            data-testid="login-email-input"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="font-mono-ui">Password</Label>
                            <Link
                                to="/forgot-password"
                                className="text-xs font-mono-ui text-muted-foreground hover:text-foreground underline underline-offset-4"
                                data-testid="login-forgot-password-link"
                            >
                                Forgot?
                            </Link>
                        </div>
                        <Input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="rounded-2xl bg-background/60 border-border h-11"
                            data-testid="login-password-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full dw-btn-primary h-12 font-mono-ui"
                        data-testid="login-submit-button"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                    No account?{" "}
                    <Link to="/register" className="text-foreground underline underline-offset-4" data-testid="login-register-link">
                        Create one
                    </Link>
                </p>
            </div>
            <div className="h-24" />
        </div>
    );
}
